import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback, useMemo } from "react";
import { FinancialDocumentListItem } from "@common/types/financialDocument";
import {
    CounterpartyCombobox,
    CounterpartyComboboxValue,
} from "@components/transactions/TransactionSplitV2/CounterpartyCombobox";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { Picker } from "@design-system/Picker/Picker.lib";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { useUpdateDocumentCounterpartyMutation } from "@mutations/financialDocument";
import styles from "../FinancialDocumentsView.module.scss";
import { useIsFinancialDocumentsTableReadonly } from "../useIsFinancialDocumentsTableReadonly";

export const CounterpartyCell = memo(
    ({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        FinancialDocumentListItem,
        FinancialDocumentListItem["counterparty"]
    >) => {
        const document = row.original;
        const isTableReadonly = useIsFinancialDocumentsTableReadonly(table);
        const { mutate: updateCounterparty } =
            useUpdateDocumentCounterpartyMutation();
        const counterparty = document.counterparty ?? undefined;
        const hasValue = !!counterparty;

        const handleSelected = useCallback(
            (selected: CounterpartyComboboxValue | undefined) => {
                updateCounterparty({
                    document,
                    counterparty: selected
                        ? { id: selected.id, name: selected.name }
                        : null,
                });
            },
            [updateCounterparty, document],
        );

        const trigger = useMemo(
            () => (
                <Picker.CellContent
                    addText="Select counterparty"
                    className={styles.counterpartyCell}
                    value={
                        hasValue ? (
                            <BreakableText
                                text={counterparty.name}
                                showTooltip
                            />
                        ) : undefined
                    }
                    aria-label={hasValue ? counterparty.name : undefined}
                />
            ),
            [hasValue, counterparty],
        );

        if (isTableReadonly) {
            return (
                <ViewTableCell
                    captureClicks
                    className={styles.counterpartyCell}
                >
                    {hasValue ? (
                        <EditableCellContent
                            readonly
                            truncationFullText={counterparty.name}
                        >
                            {counterparty.name}
                        </EditableCellContent>
                    ) : null}
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell captureClicks className={styles.counterpartyCell}>
                <CounterpartyCombobox
                    selectedCounterparty={counterparty}
                    onChange={handleSelected}
                    trigger={trigger}
                />
            </ViewTableCell>
        );
    },
);
CounterpartyCell.displayName = "CounterpartyCell";
